<template>
  <div id="timeline-results-wrapper">
    <LoaderComponent v-if="!loaded" />
    <div v-else>
      <!-- Timeline "banner" section, options for searching -->
      <div id="timeline-results-search-container">
        <!-- Display the current search parameters -->
        <p id="current-search">
          Timeline | {{ fullStart }} to {{ fullEnd }} |
          <span>{{ fullCountry }} | {{ pageInfo.total }} Results</span>
        </p>
        <div id="search-fields">
          <div id="select-container">
            <div id="dates-wrapper">
              <span>Start Date</span>
              <select id="start-date" v-model="start">
                <option selected disabled value="">Start Date</option>
                <option v-for="date in years" :key="date[0]" :value="date[0]">
                  {{ date[1] }}
                </option>
              </select>

              <span>End Date</span>
              <select id="end-date" v-model="end">
                <option selected disabled value="">End Date</option>
                <option v-for="date in years" :key="date[0]" :value="date[0]">
                  {{ date[1] }}
                </option>
              </select>
            </div>

            <div id="country-go-wrapper">
              <span>Country</span>
              <select id="country" v-model="country">
                <option selected disabled value="">Select a Country</option>
                <option
                  v-for="country in countries"
                  :key="country[0]"
                  :value="country[0]"
                >
                  {{ country[1] }}
                </option>
              </select>

              <div id="results-button">
                <button @click="goToResults()">Go</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Related content section -->
        <div
          id="related-container"
          class="related-placement"
          :class="{
            'with-gallery-placement': showGallery,
            'without-gallery-placement': !showGallery,
            'related-placement': displayPageNavigation,
            'related-no-page-placement': !displayPageNavigation,
          }"
          v-if="resultsExist"
        >
          <div id="gallery-wrapper" v-if="showGallery">
            <p class="related-header">RELATED CONTENT</p>
            <div>
              <p id="related-gallery">
                <font-awesome-icon :icon="['fas', 'share']" />
                <router-link
                  :to="{
                    name: 'timeline-gallery',
                    params: {
                      country: country,
                      start: start,
                      end: end,
                      page: 1,
                    },
                  }"
                >
                  See Gallery
                </router-link>
              </p>
            </div>
          </div>
          <div>
            <p class="related-header">
              <font-awesome-icon :icon="['fas', 'download']" />Download
            </p>
            <p class="download">
              <!-- <p class="download" @click="printPage()"> -->
              <!-- <i class="fas fa-share" v-if="printClicked === false"></i>
            <i class="fas fa-spinner" v-if="printClicked === true"></i>
            As PDF -->
              <a :href="api + '&ef=html'" target="_blank"
                ><font-awesome-icon :icon="['fas', 'up-right-from-square']" /> All Events</a
              >
            </p>
          </div>
        </div>
      </div>

      <BackComponent />

      <div
        id="top-page-navigation"
        class="page-navigation-wrapper"
        v-if="displayPageNavigation"
      >
        <PagesComponent
          :pageInfo="pageInfo"
          :navigatePage="navigatePage"
          :navigatePageDirect="navigatePageDirect"
        ></PagesComponent>
      </div>

      <!-- Main page content -->
      <div
        id="timeline-results-container"
        :class="{
          'with-gallery': showGallery && !displayPageNavigation,
          'without-gallery': !showGallery && !displayPageNavigation,
        }"
      >
        <!-- Labels for sections -->
        <div id="labels-container" v-if="resultsExist">
          <div id="date-label">Date</div>
          <div id="country-label">Country | Description</div>
        </div>
        <!-- List of event results -->
        <div id="timeline-results-wrapper" v-if="resultsExist">
          <div
            id="timeline-results"
            v-for="event in eventsLabeled"
            :key="event.eventId"
          >
            <div class="event-container">
              <div class="year">
                <span>{{ event.A }}</span>
              </div>
              <div class="event">
                <div class="country">{{ event.countryName }}</div>
                <div class="description" v-html="event.description"></div>
              </div>
            </div>
          </div>
        </div>
        <div id="timeline-no-results" v-else>
          No results. Please use the drop-down fields above to start a new
          search.
        </div>
      </div>

      <div
        class="page-navigation-wrapper"
        v-if="displayPageNavigation"
        :class="{
          'with-gallery': showGallery,
          'without-gallery': !showGallery,
        }"
      >
        <PagesComponent
          :pageInfo="pageInfo"
          :navigatePage="navigatePage"
          :navigatePageDirect="navigatePageDirect"
        ></PagesComponent>
      </div>
      <div id="padding" v-if="!displayPageNavigation && !lessThan849"></div>
    </div>
  </div>
</template>



<script>
import BackComponent from "./BackComponent.vue";
import LoaderComponent from "./LoaderComponent.vue";
import PagesComponent from "./PagesComponent.vue";
import media from "../mixins/media.js";
// Contains functions for getting timeline info
import timeline from "../mixins/timeline.js";
import pageDisplay from "../mixins/pageDisplay.js";
export default {
  name: "TimelineResults",
  components: {
    BackComponent: BackComponent,
    LoaderComponent: LoaderComponent,
    PagesComponent: PagesComponent,
  },
  mixins: [media, timeline, pageDisplay],
  data() {
    return {
      lessThan849: window.matchMedia("(max-width: 849px)").matches,
      loaded: false,
      api: "",
      countries: {},
      years: {},
      country: "",
      fullCountry: "",
      start: "",
      fullStart: "",
      end: "",
      fullEnd: "",
      events: [],
      resultsExist: true,
      displayPageNavigation: false,
      // Path, current/last page, total pages, etc.
      pageInfo: {},
      // Links to first/last/next/previous pages
      pages: {},
      showGallery: false,
      // printClicked: false,
    };
  },
  computed: {
    // Add A.D. or B.C. labels to years
    eventsLabeled: function () {
      let events = this.events;
      let keys = Object.keys(events);
      for (let i = 0; i < keys.length; i++) {
        if (Math.sign(events[keys[i]].A) === 1) {
          events[keys[i]].A = `${events[keys[i]].A} A.D.`;
        } else if (Math.sign(events[keys[i]].A) === -1) {
          events[keys[i]].A = `${events[keys[i]].A} B.C.`;
        }
      }
      return events;
    },
  },
  watch: {
    $route() {
      if (this.$route.name === "timeline-results") {
        this.getEvents();
      }
    },
  },
  methods: {
    // Set data for displaying filters in "banner"
    setCurrentFilters: function () {
      this.setFullLabels("country");
      this.setFullLabels("start-date");
      this.setFullLabels("end-date");
    },
    // Get text from select fields
    setFullLabels: function (id) {
      let sel = document.getElementById(id);
      if (id === "country") {
        this.fullCountry = sel.options[sel.selectedIndex].text;
      } else if (id === "start-date") {
        this.fullStart = sel.options[sel.selectedIndex].text;
      } else if (id === "end-date") {
        this.fullEnd = sel.options[sel.selectedIndex].text;
      }
    },
    // Execute new search
    goToResults: function () {
      let query = {
        c: this.country,
        start: this.start,
        end: this.end,
      };
      this.$router.push({ name: "timeline-results", query: query });
    },
    getEvents: function () {
      let queries = this.$route.query;
      this.country = queries.c;
      this.start = queries.start;
      this.end = queries.end;
      let baseURL = `${process.env.VUE_APP_DXA_ENDPOINT}/events?hash=${process.env.VUE_APP_API_HASH}`;
      let addURL;
      if (queries.c === "all") {
        addURL = `&na=${queries.start}&nz=${queries.end}`;
      } else {
        addURL = `&ic[]=${queries.c}&na=${queries.start}&nz=${queries.end}`;
      }
      if (queries.page) {
        addURL = `${addURL}&page=${queries.page}`;
      }
      let fullURL = baseURL + addURL;
      this.api = fullURL;
      return axios({ method: "GET", url: fullURL }).then(
        (result) => {
          this.events = result.data.items;
          this.pageInfo = result.data.page;
          this.pages = result.data.links;
          Promise.all([this.loadCountries(""), this.loadYears("")]).then(() => {
            this.setCurrentFilters();
          });
          this.setShowGallery();
          this.setResultsExist();
          this.setDisplayPageNavigation();
          this.loaded = true;
        },
        (error) => {
          this.$router.replace({ name: "error" });
          console.error(error);
        }
      );
    },
    // Query gallery results with current search parameters
    // If there are no objects returned, hide the link
    // If there are objects returned, show the link, which goes to Timeline Gallery page
    setShowGallery: function () {
      let baseURL = `${process.env.VUE_APP_DXA_ENDPOINT}/objects?hash=${process.env.VUE_APP_API_HASH}`;
      let addURL, fullURL;
      if (this.country !== "all") {
        addURL = `&ic[]=${this.country}&na=${this.start}&nz=${this.end}`;
      } else {
        addURL = `&na=${this.start}&nz=${this.end}`;
      }
      fullURL = baseURL + addURL;
      return axios({ method: "GET", url: fullURL }).then(
        (result) => {
          if (result.data.items.length === 0) {
            this.showGallery = false;
          } else {
            this.showGallery = true;
          }
        },
        (error) => {
          console.error(error);
        }
      );
    },
    setResultsExist: function () {
      if (Object.entries(this.events).length === 0) {
        this.resultsExist = false;
      } else {
        this.resultsExist = true;
      }
    },
    // Navigate to appropriate page (first/last/next/previous)
    navigatePage: function (page) {
      let url;
      if (page === "first") {
        url = this.pages[0].href;
      } else if (page === "last") {
        url = this.pages[1].href;
      } else if (page === "next") {
        url = this.pages[2].href;
      } else if (page === "previous") {
        url = this.pages[3].href;
      }
      return axios({ method: "GET", url: url }).then(
        (result) => {
          this.events = result.data.items;
          this.pageInfo = result.data.page;
          this.pages = result.data.links;
          let currentQuery = JSON.parse(JSON.stringify(this.$route.query));
          let updateQuery = currentQuery;
          updateQuery["page"] = this.pageInfo.currentPage;
          this.$router.push({ name: "timeline-results", query: updateQuery });
        },
        (error) => {
          console.error(error);
        }
      );
    },
    // Navigate directly to a page by clicking a number
    navigatePageDirect: function (page) {
      let currentQuery = JSON.parse(JSON.stringify(this.$route.query));
      let updateQuery = currentQuery;
      updateQuery["page"] = page;
      this.$router.push({ name: "timeline-results", query: updateQuery });
    },
  },
  created() {
    this.getEvents();
  },
};
</script>



<style scoped lang="scss">
#timeline-results-wrapper {
  width: 100%;
  background-color: white;
}
#timeline-results-wrapper .fas {
  font-size: 75%;
  padding-right: 4px;
}

.loader-container {
  min-height: 500px;
}

#timeline-results-search-container {
  @include flex-center;
  flex-direction: column;
  align-items: flex-start;
  position: relative;
  background-color: $theme-medium;
  color: $light-text;
  padding: 20px 50px;
}
#current-search {
  font-size: 110%;
}
#search-fields {
  @include flex-center;
  margin-top: 15px;
}
#select-container {
  @include flex-center;
}
#search-fields span {
  padding: 4px 4px 4px 0;
  margin-right: 10px;
}
#search-fields select {
  padding: 4px;
  margin-right: 20px;
}
#country-go-wrapper {
  @include flex-center;
  justify-content: flex-start;
}
#results-button button {
  @include button-styles;
  background-color: $theme-light;
  color: $light-text;
  padding: 4px 15px;
  border-radius: 100px;
}

#related-container {
  width: 25%;
  position: absolute;
  padding: 20px 20px 40px 20px;
  background-color: $theme-light;
  color: $light-text;
}
// 100% of timeline search container
// + 50px for back button height
// + 65px for navigation height
// + 34px for labels height
// To line up related content box with year/country
.related-placement {
  top: calc(100% + 50px + 65px + 34px);
  right: 0;
}
// No navigation height if results are only one page
// For some reason, the 7px is needed to line it up?
.related-no-page-placement {
  top: calc(100% + 50px + 7px + 34px);
  right: 0;
}
#related-container a {
  color: $light-text;
  text-decoration: none;
}
.related-header {
  @include text-color;
  font-size: 110%;
  font-weight: bold;
  text-decoration: underline;
  margin: 30px 0 10px 0;
  // color: $theme-medium-dark;
}
.download {
  cursor: pointer;
}
#timeline-results-wrapper .fa-spinner {
  font-size: 100%;
  padding-right: 0;
}
.download .fa-spinner {
  animation-name: spin;
  animation-duration: 2s;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
}
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

#back {
  @include flex-center;
  justify-content: flex-start;
  height: 50px;
  padding: 0 50px;
}
#back .fas {
  font-size: 75%;
  padding-right: 4px;
}
#back button {
  @include button-styles;
  padding: 2px;
  font-weight: bold;
}

#top-page-navigation #page-navigation-container {
  margin-top: 0;
  margin-bottom: 30px;
}

#timeline-results-container {
  min-height: 200px;
  width: 100%;
  background-color: white;
  padding: 0 50px 30px 50px;
}

#labels-container {
  @include flex-center;
  width: 75%;
  margin-bottom: 20px;
  font-size: 110%;
}
#date-label {
  width: 30%;
  margin-right: 2%;
}
#country-label {
  width: 70%;
}

#timeline-results {
  width: 75%;
}
.event-container {
  @include flex-center;
  width: 100%;
  justify-content: flex-start;
  align-items: flex-start;
}
.year {
  width: 30%;
  margin-right: 2%;
  border-top: 2px solid $theme-light;
}
.year span {
  background-color: $theme-light;
  color: $light-text;
  padding: 2px 10px;
}
.event {
  width: 70%;
}
.country {
  font-weight: bold;
  letter-spacing: 0.5px;
  background-color: $theme-light;
  color: $light-text;
  padding: 2px 10px;
}
.description {
  padding: 15px 0 25px 0;
}

// Prevent margin collapse
.page-navigation-wrapper {
  @include flex-center;
}
#page-navigation-container {
  background-color: white;
}
#padding {
  display: inline-block;
  // Match height of page navigation container
  margin-bottom: 65px;
}

@media only screen and (max-width: 1199px) {
  #timeline-results-search-container {
    padding: 12px 50px;
  }
  #labels-container {
    font-size: 105%;
  }
}

@media only screen and (max-width: 974px) {
  #current-search {
    font-size: 105%;
  }
  #search-fields {
    justify-content: flex-start;
    width: 100%;
  }
  #search-fields span,
  #search-fields select {
    font-size: 95%;
  }
  #search-fields span {
    margin-right: 5px;
  }
  #search-fields select {
    margin-right: 10px;
  }
  .related-header {
    font-size: 105%;
  }
  #date-label {
    width: 20%;
  }
  #country-label {
    width: 80%;
  }
  .year {
    width: 20%;
    border-top: 2px solid white;
  }
  .event {
    width: 80%;
  }
}

@media only screen and (max-width: 849px) {
  #timeline-results-wrapper {
    position: relative;
  }
  #timeline-results-search-container {
    position: static;
  }
  #search-fields {
    flex-direction: column;
    align-items: flex-start;
  }
  #search-fields span {
    display: inline-block;
    width: 12%;
  }
  #select-container {
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
  }
  #select-container select {
    width: 30%;
  }
  #dates-wrapper {
    width: 100%;
    margin-bottom: 10px;
  }
  #search-fields select {
    margin-right: 20px;
  }
  #country-go-wrapper {
    width: 100%;
  }
  #related-container {
    // Height of download button only (no gallery link) is 120px
    // Height of section including gallery link is 185px
    width: calc(100% - (50px * 2));
    padding: 20px 20px 30px 20px;
    margin: 0 50px;
  }
  // 50px of bottom "margin"
  .with-gallery-placement {
    top: calc(100% - (185px + 50px));
  }
  .without-gallery-placement {
    top: calc(100% - (120px + 50px));
  }
  .related-header {
    margin-top: 10px;
  }
  #labels-container,
  #timeline-results {
    width: 100%;
  }
  .year {
    border-top: 2px solid $theme-light;
  }
  // Height of download button only (no gallery link) is 120px
  // Height of section including gallery link is 185px
  .with-gallery {
    // + 50px for a "margin"
    margin-bottom: calc(185px + 50px);
  }
  .without-gallery {
    margin-bottom: calc(120px + 50px);
  }
}

@media only screen and (max-width: 724px) {
  #search-fields span {
    width: 70px;
  }
  #select-container select {
    width: 150px;
  }
  #search-fields select {
    margin-right: 10px;
  }
  #country-go-wrapper select {
    width: 225px;
  }
  .year {
    border-top: 2px solid white;
  }
}

@media only screen and (max-width: 599px) {
  #timeline-results-search-container {
    padding: 20px 30px;
  }
  #current-search {
    display: flex;
    flex-direction: column;
  }
  #select-container select {
    width: 120px;
  }
  #search-fields {
    margin-top: 10px;
  }
  #country-go-wrapper select {
    width: 195px;
  }
  #back {
    padding: 0 30px;
  }
  #related-container {
    // Height of download button only (no gallery link) is 98px
    // Height of section including gallery link is 165px
    width: calc(100% - (30px * 2));
    padding: 10px 20px 20px 20px;
    margin: 0 30px;
  }
  .with-gallery-placement {
    top: calc(100% - (165px + 30px));
  }
  .without-gallery-placement {
    top: calc(100% - (98px + 30px));
  }
  #timeline-results-container {
    padding: 0 30px 30px 30px;
  }
  #date-label {
    width: 22%;
  }
  #country-label {
    width: 78%;
  }
  .year {
    width: 22%;
  }
  .event {
    width: 78%;
  }
  // Height of download button only (no gallery link) is 98px
  // Height of section including gallery link is 165px
  .with-gallery {
    margin-bottom: calc(165px + 30px);
  }
  .without-gallery {
    margin-bottom: calc(98px + 30px);
  }
}

@media only screen and (max-width: 499px) {
  #timeline-results-search-container {
    padding-bottom: 15px;
  }
  #dates-wrapper {
    @include flex-center;
    flex-direction: column;
    margin-bottom: 5px;
  }
  #dates-wrapper span,
  #dates-wrapper select,
  #country-go-wrapper span,
  #country-go-wrapper select {
    width: 100%;
    margin-right: 0;
  }
  #start-date {
    margin-bottom: 5px;
  }
  #country-go-wrapper {
    flex-direction: column;
  }
  #country {
    margin-bottom: 15px;
  }
  #date-label {
    margin-right: 0;
  }
  .year {
    margin-right: 0;
  }
  .year span {
    padding: 2px 5px;
  }
  .country {
    padding: 2px 5px;
  }
}

@media only screen and (max-width: 399px) {
  .year {
    width: 20%;
    background-color: $theme-light;
    padding: 2px 5px;
    border-top: none;
  }
  .year span {
    padding: 0;
  }
  .event {
    width: 80%;
    margin-left: 5px;
  }
  #country-label {
    margin-left: 5px;
  }
}

@media print {
  #current-search,
  .year span,
  .country {
    color: black;
  }
  #labels-container {
    width: 100%;
  }
  #date-label {
    width: 20%;
    margin-right: 0;
  }
  #country-label {
    width: 80%;
  }
  #timeline-results {
    width: 100%;
  }
  .year {
    width: 20%;
    margin-right: 0;
    border-top: 0;
    border-bottom: 1px solid black;
  }
  .year span {
    background-color: none;
    padding: 0;
  }
  .event {
    width: 80%;
  }
  .country {
    background-color: none;
    padding: 0;
    border-bottom: 1px solid black;
  }
  #search-fields,
  #related-container,
  #back,
  .page-navigation-wrapper {
    display: none;
  }
}
</style>